export function ensureDataLayer() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
}

export function pushEcommercePayload(ecommercePayload) {
  if (typeof window === 'undefined') {
    return;
  }
  ensureDataLayer();
  try {
    window.dataLayer.push({ ecommerce: ecommercePayload });
  } catch (e) {
    // swallow
  }
}

export function pushImpressions(products, currencyCode = 'RUB') {
  if (!products || !products.length) {
    return;
  }
  pushEcommercePayload({ currencyCode, impressions: products });
}

export function pushClick(product, currencyCode = 'RUB') {
  if (!product) {
    return;
  }
  pushEcommercePayload({ currencyCode, click: { products: [product] } });
}

export function pushDetail(product, currencyCode = 'RUB') {
  if (!product) {
    return;
  }
  pushEcommercePayload({ currencyCode, detail: { products: [product] } });
}

export function pushAdd(products, currencyCode = 'RUB') {
  if (!products || !products.length) {
    return;
  }
  pushEcommercePayload({ currencyCode, add: { products } });
}

export function pushRemove(products, currencyCode = 'RUB') {
  if (!products || !products.length) {
    return;
  }
  pushEcommercePayload({ currencyCode, remove: { products } });
}

export function pushPurchase(orderId, products, revenue, currencyCode = 'RUB') {
  if (!orderId || !products || !products.length) {
    return;
  }
  const actionField = { id: String(orderId) };
  if (typeof revenue === 'number') {
    actionField.revenue = revenue;
  }
  pushEcommercePayload({ currencyCode, purchase: { actionField, products } });
}

export function pushPromoView(promotions) {
  if (!promotions || !promotions.length) return;
  pushEcommercePayload({ promoView: { promotions } });
}

export function pushPromoClick(promotions) {
  if (!promotions || !promotions.length) return;
  pushEcommercePayload({ promoClick: { promotions } });
}

export function mapArticleToProduct({
  article,
  quantity,
  list,
  position,
  categoryName,
  brandName,
  price,
  discountPortion,
}) {
  const id = String(article && (article.id || article.article_id));
  const name = article && (article.search_headline || article.name);
  const unitPrice = typeof price === 'number'
    ? price
    : Number(article && (article.price_in_town || article.price || 0));

  const product = { id, name, price: unitPrice };
  if (brandName) product.brand = brandName;
  if (categoryName) product.category = categoryName;
  if (list) product.list = list;
  if (typeof position === 'number') product.position = position;
  if (typeof quantity === 'number') product.quantity = quantity;
  if (typeof discountPortion === 'number' && !Number.isNaN(discountPortion)) {
    const discountAbs = Number((unitPrice * discountPortion).toFixed(2));
    if (discountAbs > 0) product.discount = discountAbs;
  }
  return product;
}

export function getRenterBrandNameFromStore(store) {
  const renter = store && store.state && store.state.renter;
  if (!renter) return undefined;
  return renter.short_commercial_name || (renter.short_commercial_name_cases && renter.short_commercial_name_cases[0]);
}

export function getCategoryNameByIdFromStore(store, categoryId) {
  const categoriesById = store && store.state && store.state.categoriesById;
  const category = categoriesById && categoriesById[categoryId];
  return category ? category.name : undefined;
}

export function getSectionNameForCategoryFromStore(store, categoryId) {
  if (!store || !store.state || !store.getters) return undefined;
  const category = store.state.categoriesById && store.state.categoriesById[categoryId];
  if (!category) return undefined;
  const section = store.getters.sectionsById && store.getters.sectionsById[category.section_id];
  return section ? section.name : undefined;
}

export function getRentDiscountPortionFromStore(store) {
  try {
    const state = store && store.state;
    const renter = state && state.renter;
    const customer = state && state.customer && state.customer.current;
    if (!customer || !customer.is_professional) return undefined;
    const special = Object.prototype.hasOwnProperty.call(customer, 'special_rent_discount') ? customer.special_rent_discount : null;
    if (special !== null && typeof special === 'number') return special;
    return renter && typeof renter.rent_discount === 'number' ? renter.rent_discount : undefined;
  } catch (e) {
    return undefined;
  }
}



// IntersectionObserver-based product impression tracker
// Observes an article card element; fires once when visible by threshold.
export function createProductImpressionObserver(options = {}) {
  if (typeof window === 'undefined') {
    return { observe() {}, reset() {}, disconnect() {}, hasSeen() { return false; } };
  }

  const {
    // When not provided, will call pushImpressions([product], 'RUB') by default
    onProductImpression,
    // IO options
    root = null,
    rootMargin = '0px',
    // Require at least 70% visibility by default
    threshold = 0.85,
  } = options;

  if (!('IntersectionObserver' in window)) {
    return { observe() {}, reset() {}, disconnect() {}, hasSeen() { return false; } };
  }

  const seenIds = new Set();
  const elementToState = new Map(); // HTMLElement -> { id, buildProduct }

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const meta = elementToState.get(entry.target);
      if (!meta) continue;
      if (!entry.isIntersecting) continue;
      const { id, buildProduct } = meta;
      if (seenIds.has(id)) {
        try { observer.unobserve(entry.target); } catch (e) {}
        elementToState.delete(entry.target);
        continue;
      }
      if (entry.intersectionRatio >= threshold) {
        seenIds.add(id);
        try {
          const product = typeof buildProduct === 'function' ? buildProduct() : null;
          if (product) {
            if (typeof onProductImpression === 'function') {
              onProductImpression(product);
            } else {
              pushImpressions([product], 'RUB');
            }
          }
        } catch (e) {
          // swallow
        } finally {
          try { observer.unobserve(entry.target); } catch (e) {}
          elementToState.delete(entry.target);
        }
      }
    }
  }, { root, rootMargin, threshold: [0, threshold] });

  function observe(cardEl, id, buildProduct) {
    if (!cardEl || id == null) return;
    const productId = String(id);
    if (seenIds.has(productId)) return;
    elementToState.set(cardEl, { id: productId, buildProduct });
    try { observer.observe(cardEl); } catch (e) {}
  }

  function disconnect() {
    try { observer.disconnect(); } catch (e) {}
    elementToState.clear();
  }

  function reset() {
    seenIds.clear();
  }

  function hasSeen(id) {
    return seenIds.has(String(id));
  }

  return { observe, reset, disconnect, hasSeen };
}
